import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import RootRedirect, { Protected } from "@/pages/RootRedirect";

import StoreLayout from "@/pages/store/StoreLayout";
import StoreHome from "@/pages/store/StoreHome";
import StoreCategories from "@/pages/store/StoreCategories";
import StoreProductDetail from "@/pages/store/StoreProductDetail";
import StoreCart from "@/pages/store/StoreCart";
import StoreWishlist from "@/pages/store/StoreWishlist";
import StoreAccount from "@/pages/store/StoreAccount";
import StoreCheckout from "@/pages/store/StoreCheckout";
import StoreAuth from "@/pages/store/StoreAuth";
import StoreOrders from "@/pages/store/StoreOrders";
import StoreOrderDetail from "@/pages/store/StoreOrderDetail";
import StoreVouchers from "@/pages/store/StoreVouchers";
import StoreAddresses from "@/pages/store/StoreAddresses";
import StoreNotifications from "@/pages/store/StoreNotifications";
import StoreHelp from "@/pages/store/StoreHelp";
import StoreAbout from "@/pages/store/StoreAbout";
import StoreChat from "@/pages/store/StoreChat";

import AdminLayout, { AdminDashboard, AdminOrders, AdminReturns, AdminProducts, AdminEmployees, AdminAudit, AdminVouchers, AdminPayments, AdminSettings, AdminReviews, AdminPlaceholder } from "@/pages/admin/AdminLayout";
import AdminReviewsPage from "@/pages/admin/AdminReviews";
import AdminChat from "@/pages/admin/AdminChat";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminBarcodePrint from "@/pages/admin/AdminBarcodePrint";
import AdminProductAI from "@/pages/admin/AdminProductAI";
import AdminAISettings from "@/pages/admin/AdminAISettings";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminEmployeeForm from "@/pages/admin/AdminEmployeeForm";
import AdminMasterData from "@/pages/admin/AdminMasterData";
import AdminPayroll from "@/pages/admin/AdminPayroll";
import AdminKasbon from "@/pages/admin/AdminKasbon";
import AdminLeave from "@/pages/admin/AdminLeave";
import AdminOvertime from "@/pages/admin/AdminOvertime";
import AdminSpk from "@/pages/admin/AdminSpk";
import AdminWarnings from "@/pages/admin/AdminWarnings";
import AdminProduction from "@/pages/admin/AdminProduction";
import AdminProductionMaster from "@/pages/admin/AdminProductionMaster";
import AdminProductionSubs from "@/pages/admin/AdminProductionSubs";
import AdminProductionWages from "@/pages/admin/AdminProductionWages";
import AdminProductionCosts from "@/pages/admin/AdminProductionCosts";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminRemuneration from "@/pages/admin/AdminRemuneration";
import AdminSchedule from "@/pages/admin/AdminSchedule";
import AdminAppraisals from "@/pages/admin/AdminAppraisals";
import AdminAppraisalForm from "@/pages/admin/AdminAppraisalForm";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import AdminLabelPrint from "@/pages/admin/AdminLabelPrint";
import AdminShipping from "@/pages/admin/AdminShipping";
import POSTerminal from "@/pages/pos/POSTerminal";
import POSDashboard from "@/pages/pos/POSDashboard";
import POSSales from "@/pages/pos/POSSales";
import POSReports, { POSReportsKasir } from "@/pages/pos/POSReports";
import POSCustomers from "@/pages/pos/POSCustomers";
import MobileProduction from "@/pages/mobile/MobileProduction";
import MobileEmployee from "@/pages/mobile/MobileEmployee";

const ADMIN_ROLES = ["owner", "hr", "supervisor", "gudang", "logistik", "pengawas_produksi", "cutting", "mandor", "finishing_qc"];
const POS_ROLES = ["kasir", "owner", "supervisor"];

function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } })
      .then(async ({ data }) => {
        if (data.access_token) localStorage.setItem("kiyana_token", data.access_token);
        await refresh();
        toast.success(`Selamat datang, ${data.user.name}`);
        nav(data.default_route || "/store", { replace: true });
      })
      .catch(() => {
        toast.error("Login Google gagal, coba lagi");
        nav("/store/auth", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas-soft">
      <Loader2 className="w-8 h-8 animate-spin text-brand" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />

      {/* Web Store — public */}
      <Route path="/store" element={<StoreLayout />}>
        <Route index element={<StoreHome />} />
        <Route path="categories" element={<StoreCategories />} />
        <Route path="product/:id" element={<StoreProductDetail />} />
        <Route path="cart" element={<StoreCart />} />
        <Route path="wishlist" element={<StoreWishlist />} />
        <Route path="account" element={<StoreAccount />} />
        <Route path="checkout" element={<StoreCheckout />} />
        <Route path="orders" element={<StoreOrders />} />
        <Route path="order/:id" element={<StoreOrderDetail />} />
        <Route path="vouchers" element={<StoreVouchers />} />
        <Route path="addresses" element={<StoreAddresses />} />
        <Route path="notifications" element={<StoreNotifications />} />
        <Route path="help" element={<StoreHelp />} />
        <Route path="about" element={<StoreAbout />} />
        <Route path="chat" element={<StoreChat />} />
      </Route>
      <Route path="/store/auth" element={<StoreAuth />} />

      {/* Employee HRIS */}

      {/* Cetak label A6 — tanpa sidebar agar bersih saat print */}
      <Route path="/admin/print/label/:orderId" element={<Protected allow={ADMIN_ROLES}><AdminLabelPrint /></Protected>} />

      {/* Admin */}
      <Route path="/admin" element={<Protected allow={ADMIN_ROLES}><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="returns" element={<AdminReturns />} />
        <Route path="shipping/settings" element={<AdminShipping />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="chat" element={<AdminChat />} />
        <Route path="pos" element={<Navigate to="/admin/pos/dashboard" replace />} />
        <Route path="pos/dashboard" element={<POSDashboard />} />
        <Route path="pos/sales" element={<POSSales />} />
        <Route path="pos/reports" element={<POSReports />} />
        <Route path="pos/customers" element={<POSCustomers />} />
        <Route path="pos/barcode" element={<AdminBarcodePrint />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="products/new" element={<AdminProductForm />} />
        <Route path="products/:id/edit" element={<AdminProductForm />} />
        <Route path="products/barcode" element={<AdminBarcodePrint />} />
        <Route path="products/ai" element={<AdminProductAI />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="vouchers" element={<AdminVouchers />} />
        <Route path="reviews" element={<AdminReviewsPage />} />
        <Route path="suppliers" element={<AdminSuppliers />} />
        <Route path="employees" element={<AdminEmployees />} />
        <Route path="employees/new" element={<AdminEmployeeForm />} />
        <Route path="employees/:id/edit" element={<AdminEmployeeForm />} />
        <Route path="appraisals" element={<AdminAppraisals />} />
        <Route path="appraisals/new" element={<AdminAppraisalForm />} />
        <Route path="appraisals/:id" element={<AdminAppraisalForm />} />
        <Route path="attendance" element={<AdminAttendance />} />
        <Route path="schedule" element={<AdminSchedule />} />
        <Route path="leave" element={<AdminLeave />} />
        <Route path="overtime" element={<AdminOvertime />} />
        <Route path="payroll" element={<AdminPayroll />} />
        <Route path="kasbon" element={<AdminKasbon />} />
        <Route path="remuneration" element={<AdminRemuneration />} />
        <Route path="tasks" element={<AdminSpk />} />
        <Route path="announcements" element={<AdminAnnouncements />} />
        <Route path="violations" element={<AdminWarnings />} />
        <Route path="production" element={<AdminProduction />} />
        <Route path="production/master" element={<AdminProductionMaster />} />
        <Route path="production/submissions" element={<AdminProductionSubs />} />
        <Route path="production/wages" element={<AdminProductionWages />} />
        <Route path="production/costs" element={<AdminProductionCosts />} />
        <Route path="reports" element={<AdminPlaceholder title="Laporan" desc="Omzet, produk terlaris, HR, keuangan." />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="settings/ai" element={<AdminAISettings />} />
        <Route path="settings/master" element={<AdminMasterData />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>

      {/* POS — terminal kasir, khusus transaksi */}
      <Route path="/pos" element={<Protected allow={POS_ROLES}><POSTerminal /></Protected>} />
      {/* POS — laporan kas untuk kasir (tanpa layout admin) */}
      <Route path="/pos/reports" element={<Protected allow={POS_ROLES}><POSReportsKasir /></Protected>} />

      {/* Produksi Mobile — tampilan HP untuk mandor/pengawas */}
      <Route path="/produksi" element={<Protected allow={[...ADMIN_ROLES, "penjahit", "crew", "karyawan", "host_live"]}><MobileProduction /></Protected>} />
      {/* Karyawan Mobile — portal HR untuk karyawan tetap (non-produksi) */}
      <Route path="/karyawan" element={<Protected allow={["crew", "karyawan", "host_live"]}><MobileEmployee /></Protected>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors duration={4000} closeButton />
    </AuthProvider>
  );
}

export default App;

